import { combineReducers, createReducer } from '@reduxjs/toolkit';

import type {
  AdvancementVersion,
  AwardVersionNormalized,
  SSElectiveVersion,
  SSElectiveVersionNormalized,
} from '@appTypes/common/versions';
import type { AwardVersion } from '@appTypes/esb';
import { advancementTypes } from '@modules/shared/constants';

import {
  advancementVersionError,
  advancementVersionResponse,
  changeMultipleBlueCardModal,
  editAdvRqmtsError,
  editAdvRqmtsRequest,
  editAdvRqmtsResponse,
  editAdvRqmtsStopLoadingRequest,
  editAdvRqmtsStopLoadingSuccess,
} from './actionsTyped';
import reducers from './reducers';

const advReqEditLoading = createReducer(false, builder => {
  builder
    .addCase(editAdvRqmtsRequest, () => true)
    .addCase(editAdvRqmtsResponse, () => false)
    .addCase(editAdvRqmtsError, () => false);
});

const advReqEditStopLoading = createReducer(false, builder => {
  builder
    .addCase(editAdvRqmtsStopLoadingRequest, () => true)
    .addCase(editAdvRqmtsStopLoadingSuccess, () => false);
});

const multipleBlueCardModalOpen = createReducer(false, builder => {
  builder.addCase(changeMultipleBlueCardModal, state => !state);
});

const advancementsVersions = createReducer(
  {} as Record<string, AdvancementVersion[]>,
  builder => {
    builder
      .addCase(advancementVersionResponse, (state, action) => {
        const {
          advancementType,
          advancementId,
          details: { versions },
        } = action.payload;
        const queryKey = `${advancementType}-${advancementId}`;
        let versionResults: AdvancementVersion[] =
          versions as AdvancementVersion[];
        // use ssVersionId as versionId for ssElectives and expiredDate
        if (advancementType === advancementTypes.SSELECTIVES) {
          versionResults = versions.map(version => {
            const ssElectiveVersion = version as SSElectiveVersion;
            return {
              ...ssElectiveVersion,
              versionId: ssElectiveVersion.ssVersionId ?? -1,
              expiredDate: ssElectiveVersion.expireDate,
            } as SSElectiveVersionNormalized;
          });
        }
        // use awardVersionId as versionId for awards
        if (advancementType === advancementTypes.AWARDS) {
          versionResults = versions.map(version => {
            const awardVersion = version as AwardVersion;
            return {
              ...awardVersion,
              versionId: awardVersion.awardVersionId ?? -1,
            } as AwardVersionNormalized;
          });
        }
        state[queryKey] = versionResults;
      })
      .addCase(advancementVersionError, (state, action) => {
        const { advancementType, advancementId } = action.payload;
        const queryKey = `${advancementType}-${advancementId}`;
        state[queryKey] = [];
      });
  },
);

export default combineReducers({
  ...reducers,
  advancementsVersions,
  advReqEditLoading,
  advReqEditStopLoading,
  multipleBlueCardModalOpen,
});
